import { Helmet } from "react-helmet-async";
import { useLanguage } from "../context/LanguageContext";

const SITE = "https://monaluxe.nl";
const SITE_NAME = "Mona Luxe";
const DEFAULT_IMG = `${SITE}/apple-touch-icon.png`;

const HREFLANG_LANGS = ["en", "nl", "de", "fr", "es", "it", "pl", "cs", "uk", "ru"];
const DEFAULT_LANG = "en";

function stripLangPrefix(path) {
  // path might contain a leading /xx prefix in some callers; ensure clean path without lang.
  return path.replace(/^\/(en|nl|de|fr|es|it|pl|cs|uk|ru)(\/|$)/, "/");
}

export function SEO({ title, description, path = "", image, type = "website", children }) {
  const { language } = useLanguage();
  const cleanPath = stripLangPrefix(path).replace(/\/+$/, "") || "";
  const fullTitle = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — Authentic Pre-Owned Designer Handbags`;
  const desc = description || "Shop authentic pre-owned luxury designer handbags from Chanel, Louis Vuitton, Hermès, Gucci and more. Free authentication & 14-day returns.";
  const url = `${SITE}/${language}${cleanPath}`;
  const img = image || DEFAULT_IMG;

  return (
    <Helmet>
      <html lang={language} />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {HREFLANG_LANGS.map((l) => (
        <link key={l} rel="alternate" hrefLang={l} href={`${SITE}/${l}${cleanPath}`} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${SITE}/${DEFAULT_LANG}${cleanPath}`} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={language === "en" ? "en_US" : `${language}_${language.toUpperCase()}`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
      {children}
    </Helmet>
  );
}

export function ProductSEO({ product }) {
  const { language } = useLanguage();
  if (!product) return null;
  const price = product.discount > 0
    ? (product.price * (1 - product.discount / 100)).toFixed(2)
    : product.price?.toFixed(2);
  const img = product.images?.[0] || DEFAULT_IMG;
  const path = `/product/${product.id}`;
  const title = `${product.brand} ${product.name}`;
  const desc = product.description
    ? product.description.substring(0, 155)
    : `Shop ${product.brand} ${product.name} — authentic pre-owned designer handbag at Mona Luxe. Free authentication guarantee.`;

  const langUrl = `${SITE}/${language}${path}`;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: `${product.brand} ${product.name}`,
    image: img,
    description: desc,
    brand: { "@type": "Brand", name: product.brand },
    category: product.category || "Designer Handbags",
    offers: {
      "@type": "Offer",
      url: langUrl,
      priceCurrency: "EUR",
      price: price,
      availability: product.in_stock
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/UsedCondition",
      seller: { "@type": "Organization", name: SITE_NAME },
    },
  };

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/${language}` },
      { "@type": "ListItem", position: 2, name: "Shop", item: `${SITE}/${language}/shop` },
      { "@type": "ListItem", position: 3, name: product.brand, item: `${SITE}/${language}/shop?brand=${encodeURIComponent(product.brand)}` },
      { "@type": "ListItem", position: 4, name: product.name, item: langUrl },
    ],
  };

  return (
    <SEO title={title} description={desc} path={path} image={img} type="product">
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
    </SEO>
  );
}

export function OrganizationSchema() {
  const { language } = useLanguage();
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: `${SITE}/${language}`,
    logo: `${SITE}/apple-touch-icon.png`,
    description: "Authentic pre-owned designer handbags. Chanel, Louis Vuitton, Hermès, Gucci and more.",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      url: `${SITE}/${language}/contact`,
    },
    sameAs: ["https://www.instagram.com/monaluxe.nl/"],
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}
